/*
 * This is the main data store for collecting all the data for the app GUI in a
 * central location.
 *
 * See http://redux.js.org/ to learn how this works.
 */

import { v1 as uuidv1 } from 'uuid'
import thunkMiddleware from 'redux-thunk'
import { createStore, applyMiddleware } from 'redux'
import { enableBatching } from 'redux-batched-actions';
import * as Action from './redux_actions.js'
import { Group, Topic } from '../models/groups.js'
import { TrainingSession, ActivityPlan, WorkoutStep } from '../models/training-sessions.js'
import { Race } from '../models/races.js'
import { User } from '../models/athletes.js'
import { database } from '../common/dexie_database.js'
import { combineReducers } from 'redux'
import { getCookie, objectsAreEquivalent, newZuluDatetime, newDateFromDatestring } from '../common/helpers.js'
import { logMessage } from './logger.js'
import { addToArrayIfNotExists, removeFromArrayIfExists } from './helpers.js'

function getInitialState() {
  var state = {
    csrf_token: getCookie('csrftoken'),
    meta_data: (typeof(xhale_meta_data) == 'object') ? xhale_meta_data : null,
    mobile_mode: false,
    calendar_display_mode: 'calendar',
    current_athlete_id: null,
    active_tag_filters: [],
    selected_sessions: [],
    athletes: {},
    coaching_companies: {},
    training_sessions: {},
    training_session_uuids_by_id: {},
    training_sessions_unsaved: {},
    races: {},
    race_uuids_by_id: {},
    rpe_summaries: {},
    rpe_current_chronic_status: {},
    rpe_current_acute_status: {},
    rpe_thresholds: {},
    template_training_sessions: {},
    template_folders: {},
    templates_loaded: false,
    workout_sections_loaded: false,
    workout_sections: {},
    training_plans: {},
    training_plan_drag_state: { state: null, start_date_or_day: null, week_key: null },
    table_metadata: { 'groups': {table_name: 'groups'}, 'athletes': {table_name: 'athletes'} },
    notes: {},
    calendar_notes: {},
    content_attachments: {},
    coaches: {},
    groups: {},
    group_topics: {},
    open_collection: null,
    private_message_conversation_details: {},
    private_messages: {},
    private_messages_multi_recipient: {},
    uploaded_files: {},
    uploaded_file_keys_sorted_by_date: [],
    uploaded_file_keys_by_date: [],

    open_time_budget: {
      week: null,
      year: null,
      saving: false,
    },
    time_budgets: {
      saved: {},
      unsaved: {}, // This should be empty if everything is saved.
    },

    session_form_discipline_widget_open: false,

    session_form: {
      mode: 'closed',
      training_session: null,
      race: null,
      currently_saving: false,
      changes_need_saving: false,
      intensity_widget_open: false,
      currently_loading: [],
      file_ids_marked_to_import: [],
      form_errors: [],
    },

  }

  return state;
}

function produce_uploaded_file_keys_by_date(uploaded_files) {
  // Create a reference to the file ids sorted by date.
  var uploaded_file_keys_sorted_by_date = [];
  var uploaded_file_keys_by_date = {};
  for (var i in uploaded_files) {

    var date_string = uploaded_files[i].file_data.local_activity_start_datetime.format('Y-m-d');

    if (!(date_string in uploaded_file_keys_by_date)) {
      uploaded_file_keys_by_date[date_string] = [];
    }
    uploaded_file_keys_by_date[date_string].push(uploaded_files[i].file_data.id);

    uploaded_file_keys_sorted_by_date.push(uploaded_files[i].file_data.id);
  }
  uploaded_file_keys_sorted_by_date.sort(function (a, b) {
    return uploaded_files[a].file_data.local_activity_start_datetime - uploaded_files[b].file_data.local_activity_start_datetime;
  });

  return {
    dict: uploaded_file_keys_by_date,
    sorted: uploaded_file_keys_sorted_by_date,
  }

}

function updateTrainingPlanLength(training_plan, training_sessions, races) {

  let plan_length = 0;

  for (let i = 0; i < training_plan.session_uuids.length; i++) {
    let training_session = training_sessions[training_plan.session_uuids[i]];
    if (training_session && training_session.training_plan_day && (training_session.training_plan_day > plan_length)) {
      plan_length = training_session.training_plan_day;
    }
  }

  for (let i = 0; i < training_plan.race_uuids.length; i++) {
    let race = races[training_plan.race_uuids[i]];
    if (race && race.training_plan_day && (race.training_plan_day > plan_length)) {
      plan_length = race.training_plan_day;
    }
  }

  return plan_length;
}

function organiseTemplateFolders(template_folders, template_training_sessions, training_plans) {

  // Create a root folder.
  template_folders.root = {
    id: 'root',
    title: 'All templates',
    orphan_folders: [],
    parent: null
  };

  // Attach arrays to folders to hold lists of discipline categories and
  // intensities of the template sessions contained in those folders.
  Object.keys(template_folders).forEach(function (key) {
    let folder_id = template_folders[key].id;
    template_folders[folder_id].contained_template_ids = [];
    template_folders[folder_id].contained_training_plan_ids = [];
    template_folders[folder_id].contained_discipline_categories = [];
    template_folders[folder_id].contained_intensities = [];
  });

  // Place template sessions into the folders.
  Object.keys(template_training_sessions).forEach(function (id) {

    // Add reference to the full template folder objects.
    let template_folder_id = (template_training_sessions[id].template_folder_id) ? template_training_sessions[id].template_folder_id : 'root';
    template_training_sessions[id].template_folder = template_folders[template_folder_id];

    // Record that this session is in this folder.
    template_folders[template_folder_id].contained_template_ids.push(id);

    // Record that at least one template with this discipline category and
    // intensity lives in its folder.
    if (template_training_sessions[id].intensity && template_folders[template_folder_id].contained_intensities.indexOf(template_training_sessions[id].intensity) == -1) {
      template_folders[template_folder_id].contained_intensities.push(template_training_sessions[id].intensity);
    }
    if (template_training_sessions[id].discipline_id && template_folders[template_folder_id].contained_discipline_categories.indexOf(template_training_sessions[id].getDisciplineCategory()) == -1) {
      template_folders[template_folder_id].contained_discipline_categories.push(template_training_sessions[id].getDisciplineCategory());
    }

  });

  // Place training plans into the folders.
  Object.keys(training_plans).forEach(function (key) {

    // Add reference to the full template folder objects.
    let template_folder_id = (training_plans[key].template_folder_id) ? training_plans[key].template_folder_id : 'root';
    training_plans[key].template_folder = template_folders[template_folder_id];

    // Record that this session is in this folder.
    // NOTE that it is possible for the plan to load before the folders or for
    // the folder to not exist at all when viewing the training plan page, but
    // after the folders load this will run again and add the folder reference.
    if (typeof(template_folders[template_folder_id]) != 'undefined') {
      template_folders[template_folder_id].contained_training_plan_ids.push(training_plans[key].id);
    }

  });

  // Reset list of child folders for each folder.
  Object.keys(template_folders).forEach(function (id) {
    template_folders[id].children = [];
  });

  // Sort template folders into a nested structure.
  Object.keys(template_folders).forEach(function (id) {

    let current_folder = template_folders[id];

    // Only cycle through folders that have a parent.
    if (current_folder.id == 'root') {
      return;
    }

    // Folders with no parent folder, have the root folder for their
    // parent.
    if (!current_folder.parent) {
      current_folder.parent = 'root';
    }

    // If the parent folder is no longer shared with this user, give it the
    // root folder for a parent instead so it doesn't just disappear. Mark that
    // it is an orphan as well so that we can refer to this fact later.
    else if (typeof(template_folders[current_folder.parent]) == 'undefined') {
      current_folder.parent = 'root';
      current_folder.is_orphan = true;
    }

    // Find this folder's parent folder.
    var parent_folder = template_folders[current_folder.parent];

    // This folder is by definition a child of its parent folder, so make
    // a reference to that fact.
    parent_folder.children.push(current_folder.id);

    // The parent folder should inherit this folder's intensities list.
    for (var i = 0; i < current_folder.contained_intensities.length; i++) {
      if (parent_folder.contained_intensities.indexOf(current_folder.contained_intensities[i]) == -1) {
        parent_folder.contained_intensities.push(current_folder.contained_intensities[i]);
      }
    }

    // The parent folder should inherit this folder's discipline category list.
    for (var i = 0; i < current_folder.contained_discipline_categories.length; i++) {
      if (parent_folder.contained_discipline_categories.indexOf(current_folder.contained_discipline_categories[i]) == -1) {
        parent_folder.contained_discipline_categories.push(current_folder.contained_discipline_categories[i]);
      }
    }

  });

  return template_folders;

}

function mainReducer(state = getInitialState(), action) {
  switch (action.type) {

    // General.
    case Action.SET_MOBILE_MODE:
      return Object.assign({}, state, { mobile_mode: action.mobile_mode });

    case Action.CALENDAR_DISPLAY_MODE:
      return Object.assign({}, state, { calendar_display_mode: action.mode });

    case Action.SET_PAGE_CONTEXT:
      return Object.assign({}, state, { page_context: action.context});

    case Action.RESET_TO_INITIAL_STATE:
      return getInitialState();

    // Athlete.

    case Action.ATHLETE_SET_CURRENT:
      return Object.assign({}, state, {
        current_athlete_id: action.athlete_id
      });
    case Action.ATHLETE_SET_UNSEEN_UPDATES:

      var athlete = Object.assign(new User({}), state.athletes[action.athlete_id], {
        unseen_changes_count: action.unseen_count,
        unseen_changes_path: action.oldest_url
      });

      // Update in the client database, if operational.
      if (xhale.user_preference.offline_mode.is_enabled()) {
        database.athletes.put(athlete.getSerializable());
      }

      return Object.assign({}, state, {
        athletes: {
          ...state.athletes,
          [action.athlete_id]: athlete,
        }
      });

    case Action.ATHLETE_ADD:
      return Object.assign({}, state, {
        athletes: {
          ...state.athletes,
          [action.athlete_data.id]: Object.assign({}, action.athlete_data)
        }
      });

    case Action.RECEIVE_ATHLETES:

      var athletes = Object.assign({}, state.athletes);
      for (var i = 0; i < action.athletes.length; i++) {
        let merged_athlete_data = Object.assign({}, athletes[action.athletes[i].id], action.athletes[i]);
        athletes[merged_athlete_data.id] = new User(merged_athlete_data);
      }

      return Object.assign({}, state, {athletes: athletes});

    case Action.REMOVE_ATHLETES:

      var athletes = Object.assign({}, state.athletes);
      for (var i = 0; i < action.athlete_ids.length; i++) {
        delete athletes[action.athlete_ids[i]];
      }

      return Object.assign({}, state, {athletes: athletes});

    // Coaches.

    case Action.COACHES_RECEIVE:
      var coaches = Object.assign({}, state.coaches);
      for (var i = 0; i < action.coaches.length; i++) {
        var coach = action.coaches[i];
        coaches[coach.id] = coach;
      }
      return Object.assign({}, state, {coaches: coaches});

    // Groups.

    case Action.GROUP_SET_CURRENT:
      return Object.assign({}, state, {
        current_group_id: action.group_id
      });

    case Action.RECEIVE_GROUPS:

      var groups = Object.assign({}, state.groups);
      var topics = Object.assign({}, state.group_topics);
      for (var i = 0; i < action.groups.length; i++) {

        var new_group_data = action.groups[i];
        var existing_group_data = (new_group_data.id in groups) ? groups[new_group_data.id] : {};

        if (typeof(new_group_data.topics) != 'undefined') {
          let group_topic_ids = [];
          for (let i = 0; i < new_group_data.topics.length; i++) {
            topics[new_group_data.topics[i].id] = new Topic(Object.assign({group_id: new_group_data.id}, new_group_data.topics[i]));
            group_topic_ids.push(new_group_data.topics[i].id);
          }
          new_group_data.topics = group_topic_ids;
        }

        groups[new_group_data.id] = new Group(Object.assign(existing_group_data, new_group_data));
      }

      return Object.assign({}, state, {groups: groups, group_topics: topics});

    case Action.REMOVE_GROUPS:

      var groups = Object.assign({}, state.groups);
      for (var i = 0; i < action.group_ids.length; i++) {
        delete groups[action.group_ids[i]];
      }

      return Object.assign({}, state, {groups: groups});

    // Group Topic Messages.

    case Action.TOPIC_MESSAGE_UNSEEN_LIST:
      return Object.assign({}, state, {topics_with_new_messages: action.topic_ids, groups_with_new_messages: action.group_ids});

    case Action.TOPIC_MARK_SEEN:

      if (!state.topics_with_new_messages) {
        return state;
      }

      var topics_with_new_messages = removeFromArrayIfExists(state.topics_with_new_messages.slice(), action.topic_id);

      // If every topic in the group is now marked as read, also remove the new
      // messages flag from the group.
      let topic = state.group_topics[action.topic_id];
      let group = state.groups[topic.group_id];
      let group_has_unread_messages = false;
      for (let i = 0; i < group.topics.length; i++) {
        if (topics_with_new_messages.indexOf(group.topics[i]) > -1) {
          group_has_unread_messages = true;
          break;
        }
      }

      if (group_has_unread_messages) {
        var groups_with_new_messages = addToArrayIfNotExists(state.groups_with_new_messages.slice(), action.group_id);
      }
      else {
        var groups_with_new_messages = removeFromArrayIfExists(state.groups_with_new_messages.slice(), action.group_id);
      }

      return Object.assign({}, state, {topics_with_new_messages: topics_with_new_messages, groups_with_new_messages: groups_with_new_messages});

    // Training sessions.

    case Action.TRAINING_SESSIONS_UNSAVED_RECEIVE:

      var training_sessions_unsaved = Object.assign({}, state.training_sessions_unsaved);

      Object.keys(action.training_sessions).forEach(function (uuid) {
        let training_session = new TrainingSession(action.training_sessions[uuid]);
        training_session.save_version = 'unsaved';
        training_session.setUpdatedTimestamp();
        training_sessions_unsaved[training_session.uuid] = training_session;
      });

      return Object.assign({}, state, {training_sessions_unsaved: training_sessions_unsaved});

    case Action.TRAINING_SESSIONS_RECEIVE:

      var training_sessions = Object.assign({}, state.training_sessions);
      var training_session_uuids_by_id = Object.assign({}, state.training_session_uuids_by_id);
      var training_plans = Object.assign({}, state.training_plans);

      let session_uuids_by_training_plan_id = {};

      Object.keys(action.training_sessions).forEach(function (session_id) {

        // Store the session itself.
        let training_session = new TrainingSession(action.training_sessions[session_id]);
        training_session.save_version = 'saved';
        training_sessions[training_session.uuid] = training_session;

        // If this is part of a training plan, reference the session in the
        // plan as well.
        if (training_session.training_plan_id) {
          if (!(training_session.training_plan_id in session_uuids_by_training_plan_id)) {
            session_uuids_by_training_plan_id[training_session.training_plan_id] = [];
          }
          session_uuids_by_training_plan_id[training_session.training_plan_id].push(training_session.uuid);
        }

        training_session_uuids_by_id[training_session.id] = training_session.uuid;

      });

      // Update the session plan IDs if relevant.
      Object.keys(session_uuids_by_training_plan_id).forEach(function (training_plan_id) {

        let training_plan = Object.assign({}, state.training_plans[training_plan_id]);

        for (let i = 0; i < session_uuids_by_training_plan_id[training_plan_id].length; i++) {
          let session_uuid = session_uuids_by_training_plan_id[training_plan_id][i];
          addToArrayIfNotExists(training_plan.session_uuids, session_uuid);
        }

        training_plan.plan_length = updateTrainingPlanLength(training_plan, training_sessions, state.races);

        training_plans[training_plan.id] = training_plan;

      });

      return Object.assign({}, state, {
        training_sessions: training_sessions,
        training_session_uuids_by_id: training_session_uuids_by_id,
        training_plans: training_plans
      });

    case Action.TRAINING_SESSIONS_REMOVE:
      var training_sessions = Object.assign({}, state.training_sessions);
      for (let i = 0; i < action.training_session_uuids.length; i++) {
        delete training_sessions[action.training_session_uuids[i]];
      }
      return Object.assign({}, state, {training_sessions: training_sessions});

    case Action.TRAINING_SESSION_OPEN:
      return Object.assign({}, state, {open_training_session_uuid: action.training_session_uuid});

    // Template training sessions.

    case Action.TEMPLATE_TRAINING_SESSIONS_RECEIVE:

      var template_training_sessions = Object.assign({}, state.template_training_sessions);
      var training_session_uuids_by_id = Object.assign({}, state.training_session_uuids_by_id);

      Object.keys(action.template_training_sessions).forEach(function (key) {
        let session_id = action.template_training_sessions[key].id;
        template_training_sessions[session_id] = new TrainingSession(action.template_training_sessions[key]);
        training_session_uuids_by_id[session_id] = template_training_sessions[session_id].uuid;
      });

      var template_folders = Object.assign({}, state.template_folders);
      Object.keys(action.template_folders).forEach(function (key) {
        let folder_id = action.template_folders[key].id;
        template_folders[folder_id] = action.template_folders[key];
      });

      var training_plans = Object.assign({}, state.training_plans);
      Object.keys(action.training_plans).forEach(function (key) {
        let plan_id = action.training_plans[key].id;
        training_plans[plan_id] = action.training_plans[key];
        training_plans[plan_id].sessions_loading_state = 'not_loaded';
      });

      template_folders = organiseTemplateFolders(template_folders, template_training_sessions, training_plans);

      return Object.assign({}, state, {
        template_training_sessions: template_training_sessions,
        template_folders: template_folders,
        training_plans: training_plans,
        training_session_uuids_by_id: training_session_uuids_by_id,
        templates_loaded: true,
      });

    case Action.TEMPLATE_TRAINING_SESSIONS_REMOVE:

      var template_training_sessions = Object.assign({}, state.template_training_sessions);
      for (let i = 0; i < action.template_training_session_ids.length; i++) {
        delete template_training_sessions[action.template_training_session_ids[i]];
      }

      var template_folders = Object.assign({}, state.template_folders);
      for (let i = 0; i < action.template_folder_ids.length; i++) {
        delete template_folders[action.template_folder_ids[i]];
      }

      template_folders = organiseTemplateFolders(template_folders, template_training_sessions, state.training_plans);

      return Object.assign({}, state, {template_training_sessions: template_training_sessions, template_folders: template_folders});

    case Action.TEMPLATE_TRAINING_SESSIONS_REMOVE_ALL:

      template_folders = organiseTemplateFolders({}, {}, {});
      return Object.assign({}, state, {
        template_training_sessions: {},
        template_folders: template_folders,
        training_plans: {},
        templates_loaded: false,
      });

    // Activity Plans.

    case Action.ACTIVITY_PLANS_RECEIVE:

      var activity_plans = Object.assign({}, state.activity_plans);
      var training_sessions = Object.assign({}, state.training_sessions);
      var training_sessions_unsaved = Object.assign({}, state.training_sessions_unsaved);
      var template_training_sessions = Object.assign({}, state.template_training_sessions);

      // Add the plans.
      Object.keys(action.activity_plans).forEach(function (key) {

        let activity_plan = new ActivityPlan(action.activity_plans[key]);

        // If it doesn't have a uuid, add one.
        if (!activity_plan.uuid) {
          activity_plan.uuid = uuidv1();
        }

        // Add the plan to a general store of activity plans.
        activity_plans[activity_plan.uuid] = activity_plan;

        // Reference the plans in the training sessions.
        for (let i = 0; i < activity_plan.training_session_ids.length; i++) {

          // Find or create a training session to add a reference to the activity
          // plan.
          let training_session_id = activity_plan.training_session_ids[i];
          let training_session_uuid = state.training_session_uuids_by_id[training_session_id];
          let saved_training_session;
          let unsaved_training_session;

          if (typeof(training_sessions_unsaved[training_session_uuid]) != 'undefined') {
            unsaved_training_session = training_sessions_unsaved[training_session_uuid];
          }

          if (typeof(training_sessions[training_session_uuid]) != 'undefined') {
            saved_training_session = training_sessions[training_session_uuid];
          }
          else if (typeof(template_training_sessions[training_session_id]) != 'undefined') {
            saved_training_session = template_training_sessions[training_session_id];
          }

          if (unsaved_training_session) {
            if (typeof(unsaved_training_session.activity_plan_uuids) == 'undefined') {
              unsaved_training_session.activity_plan_uuids = [];
            }
            addToArrayIfNotExists(unsaved_training_session.activity_plan_uuids, activity_plan.uuid);
            unsaved_training_session = new TrainingSession(unsaved_training_session);

            if (unsaved_training_session.save_version == 'unsaved') {
              training_sessions_unsaved[unsaved_training_session.uuid] = unsaved_training_session;
            }

          }

          if (saved_training_session) {
            if (typeof(saved_training_session.activity_plan_uuids) == 'undefined') {
              saved_training_session.activity_plan_uuids = [];
            }
            addToArrayIfNotExists(saved_training_session.activity_plan_uuids, activity_plan.uuid);
            saved_training_session = new TrainingSession(saved_training_session);

            if (saved_training_session.template) {
              template_training_sessions[saved_training_session.id] = saved_training_session;
            }
            else {
              training_sessions[saved_training_session.uuid] = saved_training_session;
            }

          }

        }

      });

      return Object.assign({}, state, {
        activity_plans: activity_plans,
        training_sessions: training_sessions,
        training_sessions_unsaved: training_sessions_unsaved,
        template_training_sessions: template_training_sessions,
      });

    case Action.WORKOUT_SECTIONS_SET:

      let workout_sections = Object.assign({}, state.workout_sections);
      for (let i = 0; i < action.workout_sections.length; i++) {
        let workout_section = Object.assign(action.workout_sections[i]);
        if (workout_section.deleted) {
          delete workout_sections[workout_section.id];
        }
        else {
          for (let step_index = 0; step_index < workout_section.steps.length; step_index++) {
            workout_section.steps[step_index] = new WorkoutStep(workout_section.steps[step_index]);

            // Ensure the repeat step and the other steps reference the same
            // index. A template section by definition only ever has one
            // section so this index is always the same. It just has to match
            // the repeat step.
            if (workout_section.steps[step_index].isRepeatStep()) {
              workout_section.steps[step_index].index = 1;
            }
            else {
              workout_section.steps[step_index].repeat_step_index = 1;
            }

          }
          workout_sections[workout_section.id] = workout_section;
        }
      }

      return Object.assign({}, state, {
        workout_sections_loaded: true,
        workout_sections: workout_sections,
      });

    // Races.

    case Action.RACES_RECEIVE:

      var races = Object.assign({}, state.races);
      var race_uuids_by_id = Object.assign({}, state.race_uuids_by_id);
      let race_uuids_by_training_plan_id = {};

      Object.keys(action.races).forEach(function (race_id) {

        // Store the race.
        let race = new Race(action.races[race_id]);
        races[race.uuid] = race;

        // If this is part of a training plan, reference the race in the
        // plan as well.
        if (race.training_plan_id) {
          if (!(race.training_plan_id in race_uuids_by_training_plan_id)) {
            race_uuids_by_training_plan_id[race.training_plan_id] = [];
          }
          race_uuids_by_training_plan_id[race.training_plan_id].push(race.uuid);
        }

        race_uuids_by_id[race.id] = race.uuid;

      });

      // Update the race plan IDs.
      var training_plans = Object.assign({}, state.training_plans);
      Object.keys(race_uuids_by_training_plan_id).forEach(function (training_plan_id) {

        let training_plan = Object.assign({}, state.training_plans[training_plan_id]);

        for (let i = 0; i < race_uuids_by_training_plan_id[training_plan_id].length; i++) {
          let race_id = race_uuids_by_training_plan_id[training_plan_id][i];
          addToArrayIfNotExists(training_plan.race_uuids, race_uuids_by_training_plan_id[training_plan_id][i]);
        }

        training_plan.plan_length = updateTrainingPlanLength(training_plan, state.training_sessions, races);

        training_plans[training_plan.id] = training_plan;
      });

      return Object.assign({}, state, {
        races: races,
        race_uuids_by_id: race_uuids_by_id,
        training_plans: training_plans
      });

    case Action.RACES_REMOVE:
      var races = Object.assign({}, state.races);
      for (let i = 0; i < action.race_uuids.length; i++) {
        delete races[action.race_uuids[i]];
      }
      return Object.assign({}, state, {races: races});

    // Calendar notes.

    case Action.CALENDAR_NOTES_RECEIVE:

      if (!action.calendar_notes) {
        return state;
      }

      let calendar_notes = Object.assign({}, state.calendar_notes);
      Object.values(action.calendar_notes).forEach(function (calendar_note) {
        calendar_note.date = newDateFromDatestring(calendar_note.date);
        calendar_notes[calendar_note.id] = calendar_note;
      });
      return Object.assign({}, state, { calendar_notes: calendar_notes });

    // Training plan.

    case Action.TRAINING_PLAN_RECEIVE:

      var training_plans = Object.assign({}, state.training_plans);
      training_plans[action.training_plan.id] = Object.assign({}, training_plans[action.training_plan.id], action.training_plan);
      if (!training_plans[action.training_plan.id].session_uuids) {
        training_plans[action.training_plan.id].session_uuids = [];
      }
      if (!training_plans[action.training_plan.id].race_uuids) {
        training_plans[action.training_plan.id].race_uuids = [];
      }
      return Object.assign({}, state, { training_plans: training_plans });

    case Action.TRAINING_PLAN_DELETE_FROM_STORE:

      var training_plans = Object.assign({}, state.training_plans);
      delete training_plans[action.training_plan_id];
      return Object.assign({}, state, { training_plans: training_plans });

    case Action.TRAINING_PLAN_SET_DRAG_STATE:
      let drag_state = Object.assign({}, state.training_plan_drag_state);
      drag_state.state = action.state;
      if (action.start_date_or_day) {
        drag_state.start_date_or_day = action.start_date_or_day;

        if (typeof(drag_state.start_date_or_day) == 'object') {
          let week = ("0" + drag_state.start_date_or_day.getWeek()).slice(-2);
          let year = drag_state.start_date_or_day.getWeekYear();
          drag_state.week_key = week + '-' + year;
        }
        else {
          drag_state.week_key = parseInt((drag_state.start_date_or_day - 1) / 7) + 1;
        }

      }
      return Object.assign({}, state, { training_plan_drag_state: drag_state });

    case Action.TRAINING_PLAN_COPY_JUST_ADDED:

      let training_plan_copy = {
        training_plan_copy_id: action.training_plan_copy_id,
        training_sessions: [],
        races: [],
      }

      for (let i = 0; i < action.races.length; i++) {
        training_plan_copy.training_sessions.push(action.training_sessions[i].id);
      }

      for (let i = 0; i < action.races.length; i++) {
        training_plan_copy.races.push(action.races[i].id);
      }

      return Object.assign({}, state, {last_added_training_plan_copy: training_plan_copy});

    case Action.TRAINING_PLAN_COPY_DELETE_FROM_STORE:

      return Object.assign({}, state, {last_added_training_plan_copy: null});

    case Action.CONTENT_ATTACHMENTS_RECEIVE:
      let new_content_attachments = Object.assign({}, state.content_attachments);
      Object.values(action.content_attachments).forEach((content_attachment) => {
        new_content_attachments[content_attachment.id] = content_attachment;
      });
      return Object.assign({}, state, {content_attachments: new_content_attachments});

    // Time in zones.

    case Action.TIME_IN_ZONES_RECEIVE:
      // NOTE Not not copying zones dictionary yet, which could cause problems if comparing objects.
      return Object.assign({}, state, {time_in_zones: action.time_in_zones});

    // RPE summary.

    case Action.RPE_SUMMARY_RECEIVE:
      let rpe_summaries = Object.assign({}, state.rpe_summaries);
      rpe_summaries[action.athlete_id] = Object.assign({}, action.rpe_summary);
      return Object.assign({}, state, {rpe_summaries: rpe_summaries});

    case Action.RPE_THRESHOLDS_RECEIVE:
      let rpe_thresholds = Object.assign({}, state.rpe_thresholds);
      rpe_thresholds[action.athlete_id] = {
        chronic: Object.assign({}, action.rpe_thresholds.chronic),
        acute: Object.assign({}, action.rpe_thresholds.acute),
      };
      return Object.assign({}, state, {rpe_thresholds: rpe_thresholds});

    case Action.RPE_CURRENT_STATUS_RECEIVE:
      let rpe_current_chronic_status = Object.assign({}, state.rpe_current_chronic_status);
      let rpe_current_acute_status = Object.assign({}, state.rpe_current_acute_status);
      rpe_current_chronic_status[action.athlete_id] = Object.assign({}, action.chronic_status)
      rpe_current_acute_status[action.athlete_id] = Object.assign({}, action.acute_status)
      return Object.assign({}, state, {
        rpe_current_chronic_status: rpe_current_chronic_status,
        rpe_current_acute_status: rpe_current_acute_status,
      });

    // MySasy
    case Action.MYSASY_MEASUREMENTS_RECEIVE:

      let mysasy_measurements = [];
      if (action.mysasy_measurements) {
        for (i = 0; i < action.mysasy_measurements.length; i++) {
          let measurement = action.mysasy_measurements[i];
          let date_time = measurement.timestamp.split(' ');
          measurement.date = newDateFromDatestring(date_time[0]);
          mysasy_measurements.push(measurement);
        }
      }

      return Object.assign({}, state, {
        mysasy_measurements: mysasy_measurements,
      });

    // Time budget.

    case Action.TIME_BUDGET_SET_UNSAVED:

      var time_budgets = Object.assign({}, state.time_budgets);

      var time_budgets_unsaved = Object.assign({}, state.time_budgets.unsaved);

      if (action.time_budget.date instanceof Date) {
        var date_string = action.time_budget.date.format('Y-m-d');
      }
      else {
        var date_string = action.time_budget.date;
        action.time_budget.date = newDateFromDateString(action.time_budget.date);
      }

      time_budgets_unsaved[date_string] = Object.assign({}, action.time_budget);
      time_budgets.unsaved = time_budgets_unsaved;

      return Object.assign({}, state, { time_budgets: time_budgets });

    case Action.TIME_BUDGET_OPEN_WEEK:
      var open_time_budget = Object.assign({}, state.open_time_budget);
      open_time_budget.week = action.week;
      open_time_budget.year = action.year;
      return Object.assign({}, state, {open_time_budget: open_time_budget});

    case Action.TIME_BUDGET_SET_SAVING:
      // Update that the save operation has begun.
      var open_time_budget = Object.assign({}, state.open_time_budget);
      open_time_budget.saving = true;
      return Object.assign({}, state, {open_time_budget: open_time_budget});

    case Action.TIME_BUDGET_SET_FOR_ATHLETE:
      var time_budgets = Object.assign({}, state.time_budgets);
      var time_budgets_all_athletes = Object.assign({}, state.time_budgets.athletes);

      var time_budgets_this_athlete = (action.athlete_id in time_budgets_all_athletes) ? Object.assign({}, time_budgets_all_athletes[action.athlete_id]) : {};

      Object.keys(action.time_budgets).forEach(function (date_string) {
        time_budgets_this_athlete[date_string] = action.time_budgets[date_string];
      });

      time_budgets_all_athletes[action.athlete_id] = time_budgets_this_athlete;

      time_budgets.athletes = time_budgets_all_athletes;

      return Object.assign({}, state, {time_budgets});

    case Action.TIME_BUDGET_SET_SAVED:

      var time_budgets = Object.assign({}, state.time_budgets);
      var time_budgets_saved = Object.assign({}, state.time_budgets.saved);
      var time_budgets_unsaved = Object.assign({}, state.time_budgets.unsaved);

      Object.keys(action.time_budgets).forEach(function (date_string) {
        action.time_budgets[date_string].date = (typeof(action.time_budgets[date_string].date) == 'string') ? newDateFromDatestring(action.time_budgets[date_string].date) : action.time_budgets[date_string].date;
        time_budgets_saved[date_string] = action.time_budgets[date_string];

        if (date_string in state.time_budgets.unsaved && objectsAreEquivalent(time_budgets.unsaved[date_string], time_budgets_saved[date_string])) {
          delete time_budgets_unsaved[date_string];
        }

      });

      time_budgets.saved = time_budgets_saved;
      time_budgets.unsaved = time_budgets_unsaved;

      // Form has finished saving.
      var open_time_budget = Object.assign({}, state.open_time_budget);
      open_time_budget.saving = false;

      return Object.assign({}, state, { time_budgets: time_budgets, open_time_budget: open_time_budget});

    case Action.TIME_BUDGET_TOGGLE_VISIBILITY:

      var time_budgets = Object.assign({}, state.time_budgets);
      var time_budgets_all_athletes = Object.assign({}, state.time_budgets.athletes);
      var time_budgets_this_athlete = Object.assign({}, time_budgets_all_athletes[action.athlete_id]);
      var time_budget = Object.assign({}, time_budgets_this_athlete[action.date]);

      time_budget.visible = action.visible;

      time_budgets_this_athlete[time_budget.date] = time_budget;
      time_budgets_all_athletes[action.athlete_id] = time_budgets_this_athlete;
      time_budgets.athletes = time_budgets_all_athletes;

      return Object.assign({}, state, { time_budgets: time_budgets });

    // Coaching companies.

    case Action.COACHING_COMPANIES_RECEIVE:

      let coaching_companies = Object.assign({}, state.coaching_companies, action.coaching_companies);

      return Object.assign({}, state, {coaching_companies: coaching_companies});

    // Tags.

    case Action.TAG_FILTER_TOGGLE:
      var new_active_tag_filters = state.active_tag_filters.slice();
      var tag_name_index = new_active_tag_filters.indexOf(action.tag_name);
      if (tag_name_index >= 0 ) {
        new_active_tag_filters.splice(tag_name_index, 1);
      }
      else {
        new_active_tag_filters.push(action.tag_name);
      }
      return Object.assign({}, state, {active_tag_filters: new_active_tag_filters});

    case Action.TAG_FILTER_CLEAR:
      return Object.assign({}, state, {active_tag_filters: []});

    // Selected sessions.
    case Action.SESSION_SELECTION_TOGGLE:
      var selected_sessions = state.selected_sessions.slice();
      var selected_session_index = selected_sessions.indexOf(action.session_id);
      if (selected_session_index >= 0) {
        selected_sessions.splice(selected_session_index, 1);
      }
      else {
        selected_sessions.push(action.session_id)
      }
      return Object.assign({}, state, {selected_sessions: selected_sessions});

    case Action.SESSION_SELECTION_OPEN:
      return Object.assign({}, state, {selected_sessions: action.collection.session_ids, open_collection: action.collection});

    case Action.SESSION_SELECTION_CLEAR:
      return Object.assign({}, state, {selected_sessions: [], open_collection: null});

    // Uploaded files.

    case Action.UPLOADED_FILE_DATA_ADD:

      // Copy the object.
      var new_data = {};
      for (let i = 0; i < action.uploaded_file_data.length; i++) {

        if (!action.uploaded_file_data[i].file_data) {
          logMessage('Missing file data is causing an exception', action.uploaded_file_data[i]);
        }

        var existing_data = (action.uploaded_file_data[i].file_data.id in state.uploaded_files) ? state.uploaded_files[action.uploaded_file_data[i].file_data.id] : {};
        var uploaded_file_data = Object.assign(existing_data, action.uploaded_file_data[i]);

        // Convert the date to a datetime object.
        uploaded_file_data.file_data.local_activity_start_datetime = xhale.helper.new_local_datetime(uploaded_file_data.file_data.local_activity_start_datetime);

        new_data[uploaded_file_data.file_data.id] = uploaded_file_data;
      }

      // Create the new object of keyed uploaded file data.
      var uploaded_files = Object.assign({}, state.uploaded_files, new_data);

      var uploaded_file_keys_by_date = produce_uploaded_file_keys_by_date(uploaded_files);

      return Object.assign({}, state, {
        uploaded_files: uploaded_files,
        uploaded_file_keys_by_date: uploaded_file_keys_by_date.dict,
        uploaded_file_keys_sorted_by_date: uploaded_file_keys_by_date.sorted,
      });

    case Action.UPLOADED_FILE_MARK_IMPORTED:

      var uploaded_files = Object.assign({}, state.uploaded_files);
      for (var i = 0; i < action.uploaded_file_ids.length; i++) {
        if (action.uploaded_file_ids[i] in uploaded_files) {
          var uploaded_file = Object.assign({}, uploaded_files[action.uploaded_file_ids[i]]);
          var file_data = Object.assign({}, uploaded_file.file_data);
          file_data.processed = true;
          uploaded_file.file_data = file_data;
          uploaded_files[action.uploaded_file_ids[i]] = uploaded_file;
        }
      }

      var uploaded_file_keys_by_date = produce_uploaded_file_keys_by_date(uploaded_files);

      return Object.assign({}, state, {
        uploaded_files: uploaded_files,
        uploaded_file_keys_by_date: uploaded_file_keys_by_date.dict,
        uploaded_file_keys_sorted_by_date: uploaded_file_keys_by_date.sorted,
      });

    // Notes.

    case Action.RECEIVE_NOTES:

      var notes = Object.assign({}, state.notes);
      for (var i = 0; i < action.notes.length; i++) {
        var note = action.notes[i];
        notes[note.id] = note;
      }

      return Object.assign({}, state, {notes: notes});

    // Private messages.

    case Action.PRIVATE_MESSAGE_MULTI_CONVERSATION_DATA:

      let private_messages_multi_recipient = Object.assign({}, state.private_messages_multi_recipient);

      for (var i = 0; i < action.messages.length; i++) {
        let message = action.messages[i];
        message.publish_datetime = (typeof(message.publish_datetime) == 'string') ? newZuluDatetime(message.publish_datetime) : message.publish_datetime;
        message.recipient_ids = message.recipient_ids;
        private_messages_multi_recipient[message.message_message_id] = message;
      }

      return Object.assign({}, state, {
        private_messages_multi_recipient: private_messages_multi_recipient,
      });

    case Action.PRIVATE_MESSAGE_CONVERSATION_DATA:

      let private_messages = Object.assign({}, state.private_messages);
      var conversation_details = Object.assign({}, state.private_message_conversation_details[action.conversation_details.recipient_id]);

      conversation_details.recipient_id = action.conversation_details.recipient_id;

      if (!isNaN(action.conversation_details.unseen_count)) {
        conversation_details.unseen_count = action.conversation_details.unseen_count;
      }

      if (typeof(action.conversation_details.next_earlier_message_publish_datetime) != 'undefined') {
        conversation_details.next_earlier_message_publish_datetime = action.conversation_details.next_earlier_message_publish_datetime;
      }
      conversation_details.message_ids = (typeof(conversation_details.message_ids) == 'object') ? conversation_details.message_ids.slice() : [];

      for (var i = 0; i < action.conversation_details.messages.length; i++) {
        var message = action.conversation_details.messages[i];
        message.publish_datetime = (typeof(message.publish_datetime) == 'string') ? newZuluDatetime(message.publish_datetime) : message.publish_datetime;
        conversation_details.message_ids = addToArrayIfNotExists(conversation_details.message_ids, message.id);
        private_messages[message.id] = message;
      }

      conversation_details.message_ids.sort(function (a, b) {
        return private_messages[a].publish_datetime - private_messages[b].publish_datetime;
      });

      var private_message_conversation_details = Object.assign({}, state.private_message_conversation_details);
      private_message_conversation_details[action.conversation_details.recipient_id] = conversation_details;

      return Object.assign({}, state, {
        private_message_conversation_details: private_message_conversation_details,
        private_messages: private_messages,
      });

    case Action.PRIVATE_MESSAGE_UPDATE_UNSEEN:
      var conversation_details = Object.assign({}, state.private_message_conversation_details[action.recipient_id]);
      conversation_details.unseen_count = action.unseen_count;
      var private_message_conversation_details = {
        ...state.private_message_conversation_details,
        [action.recipient_id]: conversation_details,
      }
      return Object.assign({}, state, { private_message_conversation_details: private_message_conversation_details });

    // Table meta data.
    case Action.RECEIVE_TABLE_METADATA:

      var table_metadata = Object.assign({}, state.table_metadata);
      table_metadata[action.table_metadata.table_name] = action.table_metadata;
      return Object.assign({}, state, {table_metadata: table_metadata});

    // Unknown actions just return the previous state.

    default:
      return state
  }
}


// Session Form
// NOTE: This is stub code for now. We are just refactoring to separate
// reducers and want to keep this here as an example.
function sessionFormReducer(state = [], action) {
  switch (action.type) {
    case Action.ATHLETE_SESSION_FORM_SET_STATE:
      return Object.assign({}, state, {});
      break;
    default:
      return state;
  }

  return state
  
}

const reducer = combineReducers({
  main: mainReducer,
  session_form: sessionFormReducer,
})

export let store = createStore(enableBatching(reducer), applyMiddleware(thunkMiddleware));
